import { RefObject, useCallback, useEffect, useRef } from "react";

// 가장자리 감지 밴드 두께(px)와 최대 스크롤 속도(px/frame). 튜닝 가능.
const EDGE_SIZE = 72;
const MAX_SPEED = 18;

// 포인터 Y(clientY)가 컨테이너 상/하단 밴드에 들어오면 스크롤 속도를 돌려준다.
// 위로 스크롤(음수) / 아래로 스크롤(양수) / 밴드 밖이면 0. 가장자리에 가까울수록
// 빠르게(밴드 진입 깊이에 비례). 순수 함수 — 테스트 대상.
export function computeAutoScrollVelocity(
  clientY: number,
  rectTop: number,
  rectBottom: number,
  edgeSize: number = EDGE_SIZE,
  maxSpeed: number = MAX_SPEED
): number {
  const topDistance = clientY - rectTop;
  const bottomDistance = rectBottom - clientY;
  if (topDistance < edgeSize) {
    const depth = (edgeSize - Math.max(0, topDistance)) / edgeSize;
    return -maxSpeed * depth;
  }
  if (bottomDistance < edgeSize) {
    const depth = (edgeSize - Math.max(0, bottomDistance)) / edgeSize;
    return maxSpeed * depth;
  }
  return 0;
}

// 드래그 중 컨테이너 가장자리에 다가가면 자동 스크롤. dragover 가 멈춰도(가장자리
// 에서 포인터 정지) rAF 루프가 마지막 속도로 계속 스크롤한다 — 의도된 동작.
export function useTaskEditorDragAutoScroll(
  containerRef: RefObject<HTMLElement | null>
) {
  const velocityRef = useRef(0);
  const rafRef = useRef<number | null>(null);

  const tick = useCallback(() => {
    const el = containerRef.current;
    if (el && velocityRef.current !== 0) {
      el.scrollTop += velocityRef.current;
    }
    rafRef.current = requestAnimationFrame(tick);
  }, [containerRef]);

  const stop = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    velocityRef.current = 0;
  }, []);

  const update = useCallback(
    (clientY: number) => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      velocityRef.current = computeAutoScrollVelocity(
        clientY,
        rect.top,
        rect.bottom
      );
      if (velocityRef.current !== 0 && rafRef.current === null) {
        rafRef.current = requestAnimationFrame(tick);
      }
    },
    [containerRef, tick]
  );

  // 언마운트 시 루프 정리
  useEffect(() => stop, [stop]);

  return { update, stop };
}
